const libFableServiceProviderBase = require('fable-serviceproviderbase');
const libCrypto = require('crypto');
const libIntegrationAdapter = require('headlight-integration-adapter');

const SPECIFICATION_SET_LINE_ITEM_SAMPLE = 500;
// INN values ride in the URL path; the browser -> Kong path drops requests well below what Node -> API accepts.
const INN_VALUE_CHARACTER_BUDGET = 2000;

class SamplePlanService extends libFableServiceProviderBase
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		this.serviceType = 'SamplePlanService';
	}

	/**
	 * The HeadlightRestClient instance from the fable service manager.
	 * Both CLI and web contexts register this service on fable.
	 */
	get restClient()
	{
		return this.fable.HeadlightRestClient;
	}

	/**
	 * Generate a deterministic GUID from a prefix and composite key using MD5.
	 *
	 * @param {string} pPrefix - The prefix for the GUID (e.g., 'MSP-PLIMT', 'MSP-MLJ').
	 * @param {string} pCompositeKey - The composite key to hash.
	 * @return {string} The generated GUID.
	 */
	generateGUID(pPrefix, pCompositeKey)
	{
		let tmpHash = libCrypto.createHash('md5').update(pCompositeKey).digest('hex').substring(0, 12);
		return `${pPrefix}-${tmpHash}`;
	}

	/**
	 * Split INN filter values into batches whose comma-joined length stays within a character budget, so short
	 * numeric IDs batch widely and long strings batch small. A value longer than the budget goes out alone.
	 *
	 * @param {Array<string|number>} pValues - The values to filter by.
	 * @param {number} [pBudget] - Maximum comma-joined length per batch; defaults to INN_VALUE_CHARACTER_BUDGET.
	 * @return {Array<Array<string|number>>} The batches, in input order.
	 */
	batchINNValues(pValues, pBudget)
	{
		let tmpBudget = pBudget || INN_VALUE_CHARACTER_BUDGET;
		let tmpBatches = [];
		let tmpBatch = [];
		let tmpBatchLength = 0;
		for (let i = 0; i < pValues.length; i++)
		{
			let tmpValueLength = String(pValues[i]).length;
			let tmpNextLength = (tmpBatch.length > 0) ? (tmpBatchLength + 1 + tmpValueLength) : tmpValueLength;
			if ((tmpBatch.length > 0) && (tmpNextLength > tmpBudget))
			{
				tmpBatches.push(tmpBatch);
				tmpBatch = [];
				tmpNextLength = tmpValueLength;
			}
			tmpBatch.push(pValues[i]);
			tmpBatchLength = tmpNextLength;
		}
		if (tmpBatch.length > 0)
		{
			tmpBatches.push(tmpBatch);
		}
		return tmpBatches;
	}

	/**
	 * Fetch entities in batched INN filters, each within INN_VALUE_CHARACTER_BUDGET, to avoid URL length limits.
	 *
	 * @param {string} pEntityType - The Meadow entity type.
	 * @param {string} pFilterField - The field to filter on.
	 * @param {Array<string|number>} pIDs - The values to filter by (IDs, or strings such as ItemCode).
	 * @param {string|null} pExtraFilter - Additional filter expression to append.
	 * @param {Function} fCallback - Callback: (error, records[]).
	 */
	getEntitySetBatched(pEntityType, pFilterField, pIDs, pExtraFilter, fCallback)
	{
		let tmpAllRecords = [];

		if (pIDs.length === 0)
		{
			return fCallback(null, []);
		}

		let tmpBatches = this.batchINNValues(pIDs);

		let tmpBatchIndex = 0;
		let tmpProcessBatch = () =>
		{
			if (tmpBatchIndex >= tmpBatches.length)
			{
				this.log.info(`    ... fetched ${tmpAllRecords.length} ${pEntityType} records total.`);
				return fCallback(null, tmpAllRecords);
			}

			if (tmpBatches.length > 1)
			{
				this.log.info(`    ... batch ${tmpBatchIndex + 1}/${tmpBatches.length} (${tmpAllRecords.length} records so far)`);
			}

			let tmpBatchIDs = tmpBatches[tmpBatchIndex].join(',');
			let tmpFilter = `FBL~${pFilterField}~INN~${tmpBatchIDs}~FBV~Deleted~EQ~0`;
			if (pExtraFilter)
			{
				tmpFilter += `~${pExtraFilter}`;
			}

			this.restClient.getEntitySet(pEntityType, tmpFilter,
				(pError, pRecords) =>
				{
					if (pError)
					{
						return fCallback(pError);
					}
					tmpAllRecords = tmpAllRecords.concat(pRecords);
					tmpBatchIndex++;
					tmpProcessBatch();
				}, 0, 200000);
		};
		tmpProcessBatch();
	}

	/**
	 * Count entities in batched INN filters, each within INN_VALUE_CHARACTER_BUDGET, to avoid URL length limits.
	 *
	 * @param {string} pEntityType - The Meadow entity type.
	 * @param {string} pFilterField - The field to filter on.
	 * @param {Array<string|number>} pIDs - The values to filter by (IDs, or strings such as ItemCode).
	 * @param {string|null} pExtraFilter - Additional filter expression to append.
	 * @param {(pError?: any, pCount?: number) => void} fCallback - Callback with the total across batches.
	 */
	getEntitySetCountBatched(pEntityType, pFilterField, pIDs, pExtraFilter, fCallback)
	{
		let tmpBatches = this.batchINNValues(pIDs);
		let tmpTotal = 0;
		let tmpBatchIndex = 0;

		let tmpProcessBatch = () =>
		{
			if (tmpBatchIndex >= tmpBatches.length)
			{
				return fCallback(null, tmpTotal);
			}

			let tmpFilter = `FBL~${pFilterField}~INN~${tmpBatches[tmpBatchIndex].join(',')}~FBV~Deleted~EQ~0`;
			if (pExtraFilter)
			{
				tmpFilter += `~${pExtraFilter}`;
			}
			tmpBatchIndex++;

			this.restClient.getEntitySetCount(pEntityType, tmpFilter,
				(pError, pResult) =>
				{
					if (pError)
					{
						return fCallback(pError);
					}
					tmpTotal += (pResult && pResult.Count) ? pResult.Count : 0;
					tmpProcessBatch();
				});
		};
		tmpProcessBatch();
	}

	/**
	 * Index line items by ItemCode. When an ItemCode repeats, the last line item read wins, and the
	 * repeats are reported so the caller can warn.
	 *
	 * @param {Array<Record<string, any>>} pLineItems - Line items carrying ItemCode.
	 * @return {{ByItemCode: Record<string, Record<string, any>>, DuplicateItemCodes: Array<string>, Warning: string|null}} The index.
	 */
	indexLineItemsByItemCode(pLineItems)
	{
		let tmpByItemCode = {};
		let tmpDuplicates = {};
		for (let i = 0; i < pLineItems.length; i++)
		{
			let tmpItemCode = pLineItems[i].ItemCode;
			if (!tmpItemCode)
			{
				continue;
			}
			if (tmpByItemCode[tmpItemCode])
			{
				tmpDuplicates[tmpItemCode] = true;
			}
			tmpByItemCode[tmpItemCode] = pLineItems[i];
		}

		let tmpDuplicateItemCodes = Object.keys(tmpDuplicates);
		let tmpWarning = null;
		if (tmpDuplicateItemCodes.length > 0)
		{
			let tmpShown = tmpDuplicateItemCodes.slice(0, 10).join(', ') + ((tmpDuplicateItemCodes.length > 10) ? ', ...' : '');
			tmpWarning = `${tmpDuplicateItemCodes.length} item code(s) appear on more than one source line item, so the plan is copied from only one of them: ${tmpShown}.`;
		}
		return { ByItemCode: tmpByItemCode, DuplicateItemCodes: tmpDuplicateItemCodes, Warning: tmpWarning };
	}

	/**
	 * @typedef {object} SamplePlanProjectContext
	 * @property {Record<string, any>} Project - The Project record.
	 * @property {Record<string, any>|null} Contract - The project's Contract, or null when it has none.
	 * @property {Array<{IDProject: number, Name: string}>} ContractProjects - Every live project on that Contract, this one included.
	 */

	/**
	 * @typedef {object} ProjectSpecificationSets
	 * @property {number} IDProject - The project.
	 * @property {string} Name - The project name.
	 * @property {Record<string, any>|null} Contract - The project's Contract, or null.
	 * @property {Array<{IDProject: number, Name: string}>} ContractProjects - Every live project on that Contract.
	 * @property {number} ItemIDTestSpecificationSet - The Contract's item set, else the single set the line items' pay items share; 0 when neither resolves.
	 * @property {number} MaterialIDTestSpecificationSet - The Contract's material set, else the item set.
	 * @property {string} Basis - Where the item set came from: 'Contract', 'PayItems' or 'None'.
	 * @property {Record<string, number>} PayItemSetDistribution - Line item count per PayItem.IDTestSpecificationSet.
	 * @property {boolean} PayItemSetMixed - True when the line items' pay items span more than one set.
	 * @property {number} LineItemsWithoutPayItem - Line items with no live pay item.
	 * @property {number} LineItemsExamined - Line items the distribution was built from.
	 * @property {Record<string, string>} [SpecificationSetNames] - TestSpecificationSet names by ID, when fetched.
	 */

	/**
	 * @typedef {object} LineItemPayItemRemap
	 * @property {number} IDLineItem - The destination line item.
	 * @property {string} GUIDLineItem - Its existing GUID, used verbatim to update in place.
	 * @property {string} ItemCode - The ItemCode the pay items were matched on.
	 * @property {string} Name - The line item name.
	 * @property {number} FromIDPayItem - The current pay item.
	 * @property {number} FromIDTestSpecificationSet - The current pay item's set.
	 * @property {number} ToIDPayItem - The target-set pay item.
	 */

	/**
	 * @typedef {object} SpecificationSetContractUpdate
	 * @property {number} IDContract - The destination Contract.
	 * @property {string} GUIDContract - Its existing GUID, used verbatim to update in place.
	 * @property {string} Name - The Contract name.
	 * @property {{ItemIDTestSpecificationSet: number, MaterialIDTestSpecificationSet: number}} Before - Current sets.
	 * @property {{ItemIDTestSpecificationSet: number, MaterialIDTestSpecificationSet: number}} After - Target sets.
	 * @property {Array<{IDProject: number, Name: string}>} SharedWithProjects - Other projects on the same Contract.
	 */

	/**
	 * @typedef {object} SpecificationSetAlignment
	 * @property {boolean} Crossing - True when the destination sits on different sets than the source.
	 * @property {number} ItemIDTestSpecificationSet - Target item set.
	 * @property {number} MaterialIDTestSpecificationSet - Target material set.
	 * @property {SpecificationSetContractUpdate|null} Contract - The Contract update, or null when none is needed or possible.
	 * @property {Array<LineItemPayItemRemap>} LineItemRemaps - Line items to move onto target-set pay items.
	 * @property {Array<{IDLineItem: number, ItemCode: string, Name: string, Reason: string}>} Unmapped - Line items on another set that cannot be moved.
	 * @property {number} AlreadyAlignedCount - Line items already on target-set pay items.
	 * @property {number} WithoutPayItemCount - Line items with no live pay item, left alone.
	 * @property {Array<string>} Warnings - Conditions to surface before the load.
	 * @property {boolean} [Flip] - Set by `resolveSpecificationSetAlignment`: whether the copy applies the plan.
	 */

	/**
	 * Fetch a project, its Contract (if any) and every live project sharing that Contract.
	 *
	 * @param {number} pIDProject - The project.
	 * @param {(pError?: any, pContext?: SamplePlanProjectContext) => void} fCallback - Callback.
	 */
	getProjectContext(pIDProject, fCallback)
	{
		this.restClient.getEntity('Project', pIDProject,
			(pError, pProject) =>
			{
				if (pError)
				{
					return fCallback(pError);
				}
				let tmpContext = { Project: pProject || {}, Contract: null, ContractProjects: [] };
				if (!(tmpContext.Project.IDContract > 0))
				{
					return fCallback(null, tmpContext);
				}
				this.restClient.getEntity('Contract', tmpContext.Project.IDContract,
					(pContractError, pContract) =>
					{
						if (pContractError)
						{
							return fCallback(pContractError);
						}
						if (!pContract || !(pContract.IDContract > 0))
						{
							return fCallback(null, tmpContext);
						}
						tmpContext.Contract = pContract;
						this.restClient.getEntitySet('Project', `FBV~IDContract~EQ~${pContract.IDContract}~FBV~Deleted~EQ~0`,
							(pProjectsError, pProjects) =>
							{
								if (pProjectsError)
								{
									return fCallback(pProjectsError);
								}
								tmpContext.ContractProjects = (pProjects || []).map((pSharingProject) => ({ IDProject: pSharingProject.IDProject, Name: pSharingProject.Name || '' }));
								return fCallback(null, tmpContext);
							}, 0, 1000);
					});
			});
	}

	/**
	 * Fetch the live pay items referenced by a set of line items.
	 *
	 * @param {Array<Record<string, any>>} pLineItems - Line items carrying IDPayItem.
	 * @param {(pError?: any, pPayItemsByID?: Record<string, Record<string, any>>) => void} fCallback - Callback.
	 */
	getPayItemsForLineItems(pLineItems, fCallback)
	{
		let tmpIDs = [ ...new Set(pLineItems.map((pLineItem) => pLineItem.IDPayItem).filter((pID) => pID > 0)) ];
		this.getEntitySetBatched('PayItem', 'IDPayItem', tmpIDs, null,
			(pError, pPayItems) =>
			{
				if (pError)
				{
					return fCallback(pError);
				}
				let tmpPayItemsByID = {};
				for (let i = 0; i < pPayItems.length; i++)
				{
					tmpPayItemsByID[pPayItems[i].IDPayItem] = pPayItems[i];
				}
				return fCallback(null, tmpPayItemsByID);
			});
	}

	/**
	 * Fetch TestSpecificationSet names.
	 *
	 * @param {Array<number>} pSetIDs - Set IDs; zeros and repeats are ignored.
	 * @param {(pError?: any, pNames?: Record<string, string>) => void} fCallback - Callback.
	 */
	getSpecificationSetNames(pSetIDs, fCallback)
	{
		let tmpIDs = [ ...new Set(pSetIDs.filter((pID) => pID > 0)) ];
		this.getEntitySetBatched('TestSpecificationSet', 'IDTestSpecificationSet', tmpIDs, null,
			(pError, pSets) =>
			{
				if (pError)
				{
					return fCallback(pError);
				}
				let tmpNames = {};
				for (let i = 0; i < pSets.length; i++)
				{
					tmpNames[pSets[i].IDTestSpecificationSet] = pSets[i].Name || '';
				}
				return fCallback(null, tmpNames);
			});
	}

	/**
	 * Work out which specification sets a project is on. The Contract is authoritative; a project
	 * without one falls back to PayItem.IDTestSpecificationSet across its line items.
	 *
	 * @param {SamplePlanProjectContext} pContext - The project and its Contract.
	 * @param {Array<Record<string, any>>} pLineItems - The line items to read pay item sets from.
	 * @param {Record<string, Record<string, any>>} pPayItemsByID - Pay items by IDPayItem.
	 * @return {ProjectSpecificationSets} The project's sets.
	 */
	summarizeProjectSpecificationSets(pContext, pLineItems, pPayItemsByID)
	{
		let tmpDistribution = {};
		let tmpWithoutPayItem = 0;
		for (let i = 0; i < pLineItems.length; i++)
		{
			let tmpPayItem = pPayItemsByID[pLineItems[i].IDPayItem];
			if (!tmpPayItem)
			{
				tmpWithoutPayItem++;
				continue;
			}
			let tmpSetID = Number(tmpPayItem.IDTestSpecificationSet) || 0;
			tmpDistribution[tmpSetID] = (tmpDistribution[tmpSetID] || 0) + 1;
		}
		let tmpPayItemSetIDs = Object.keys(tmpDistribution);
		let tmpPayItemSetID = (tmpPayItemSetIDs.length === 1) ? parseInt(tmpPayItemSetIDs[0], 10) : 0;

		let tmpContract = pContext.Contract;
		let tmpItemSetID = 0;
		let tmpBasis = 'None';
		if (tmpContract && (Number(tmpContract.ItemIDTestSpecificationSet) > 0))
		{
			tmpItemSetID = Number(tmpContract.ItemIDTestSpecificationSet);
			tmpBasis = 'Contract';
		}
		else if (tmpPayItemSetID > 0)
		{
			tmpItemSetID = tmpPayItemSetID;
			tmpBasis = 'PayItems';
		}
		let tmpMaterialSetID = (tmpContract && (Number(tmpContract.MaterialIDTestSpecificationSet) > 0)) ? Number(tmpContract.MaterialIDTestSpecificationSet) : tmpItemSetID;

		return (
			{
				IDProject: pContext.Project.IDProject,
				Name: pContext.Project.Name || '',
				Contract: tmpContract,
				ContractProjects: pContext.ContractProjects,
				ItemIDTestSpecificationSet: tmpItemSetID,
				MaterialIDTestSpecificationSet: tmpMaterialSetID,
				Basis: tmpBasis,
				PayItemSetDistribution: tmpDistribution,
				PayItemSetMixed: tmpPayItemSetIDs.length > 1,
				LineItemsWithoutPayItem: tmpWithoutPayItem,
				LineItemsExamined: pLineItems.length
			});
	}

	/**
	 * Work out a project's specification sets from its Contract, else from a sample of its own line items.
	 *
	 * @param {SamplePlanProjectContext} pContext - The project and its Contract.
	 * @param {number} [pLineItemLimit] - Maximum line items to sample for the pay item fallback.
	 * @param {(pError?: any, pSets?: ProjectSpecificationSets) => void} fCallback - Callback.
	 */
	resolveProjectSpecificationSets(pContext, pLineItemLimit, fCallback)
	{
		this.restClient.getEntitySet('LineItem', `FBV~IDProject~EQ~${pContext.Project.IDProject}~FBV~Deleted~EQ~0`,
			(pError, pRecords) =>
			{
				if (pError)
				{
					return fCallback(pError);
				}
				let tmpLineItems = pRecords || [];
				this.getPayItemsForLineItems(tmpLineItems,
					(pPayItemError, pPayItemsByID) =>
					{
						if (pPayItemError)
						{
							return fCallback(pPayItemError);
						}
						return fCallback(null, this.summarizeProjectSpecificationSets(pContext, tmpLineItems, pPayItemsByID));
					});
			}, 0, pLineItemLimit || SPECIFICATION_SET_LINE_ITEM_SAMPLE);
	}

	/**
	 * Describe a project's specification sets for display.
	 *
	 * @param {number} pIDProject - The project.
	 * @param {number|null} pLineItemLimit - Maximum line items to sample for the pay item fallback; null for the default.
	 * @param {(pError?: any, pSets?: ProjectSpecificationSets) => void} fCallback - Callback.
	 */
	describeProjectSpecificationSets(pIDProject, pLineItemLimit, fCallback)
	{
		let tmpSets = null;

		this.fable.Utility.waterfall(
			[
				(fStageComplete) =>
				{
					this.getProjectContext(pIDProject,
						(pError, pContext) =>
						{
							if (pError)
							{
								return fStageComplete(pError);
							}
							this.resolveProjectSpecificationSets(pContext, pLineItemLimit,
								(pSetsError, pSets) =>
								{
									tmpSets = pSets;
									return fStageComplete(pSetsError);
								});
						});
				},
				(fStageComplete) =>
				{
					this.getSpecificationSetNames([ tmpSets.ItemIDTestSpecificationSet, tmpSets.MaterialIDTestSpecificationSet ],
						(pError, pNames) =>
						{
							tmpSets.SpecificationSetNames = pNames || {};
							return fStageComplete(pError);
						});
				}
			],
			(pError) =>
			{
				if (pError)
				{
					this.log.error(`Error describing specification sets for project ${pIDProject}`, pError);
				}
				return fCallback(pError, tmpSets);
			});
	}

	/**
	 * Plan the Contract and LineItem updates that move a destination project onto the source's
	 * specification sets. Line items are matched to target-set pay items by ItemCode through
	 * PayItem.IDTestSpecificationSet.
	 *
	 * @param {object} pInputs - Planning inputs.
	 * @param {number} pInputs.ItemIDTestSpecificationSet - Target item set; 0 disables realignment.
	 * @param {number} pInputs.MaterialIDTestSpecificationSet - Target material set; defaults to the item set.
	 * @param {number} pInputs.DestIDProject - The destination project.
	 * @param {Record<string, any>|null} pInputs.DestContract - The destination's Contract, or null.
	 * @param {Array<{IDProject: number, Name: string}>} pInputs.DestContractProjects - Every live project on that Contract.
	 * @param {Array<Record<string, any>>} pInputs.DestLineItems - Every destination line item.
	 * @param {Record<string, Record<string, any>>} pInputs.DestPayItemsByID - The destination line items' pay items.
	 * @param {Record<string, Array<Record<string, any>>>} pInputs.TargetPayItemsByItemCode - Target-set pay items by ItemCode.
	 * @return {SpecificationSetAlignment} The plan.
	 */
	planSpecificationSetAlignment(pInputs)
	{
		let tmpItemSetID = Number(pInputs.ItemIDTestSpecificationSet) || 0;
		let tmpMaterialSetID = Number(pInputs.MaterialIDTestSpecificationSet) || tmpItemSetID;
		let tmpAlignment = (
			{
				Crossing: false,
				ItemIDTestSpecificationSet: tmpItemSetID,
				MaterialIDTestSpecificationSet: tmpMaterialSetID,
				Contract: null,
				LineItemRemaps: [],
				Unmapped: [],
				AlreadyAlignedCount: 0,
				WithoutPayItemCount: 0,
				Warnings: []
			});

		if (tmpItemSetID < 1)
		{
			return tmpAlignment;
		}

		let tmpContract = pInputs.DestContract;
		if (tmpContract)
		{
			let tmpBefore = (
				{
					ItemIDTestSpecificationSet: Number(tmpContract.ItemIDTestSpecificationSet) || 0,
					MaterialIDTestSpecificationSet: Number(tmpContract.MaterialIDTestSpecificationSet) || 0
				});
			if ((tmpBefore.ItemIDTestSpecificationSet !== tmpItemSetID) || (tmpBefore.MaterialIDTestSpecificationSet !== tmpMaterialSetID))
			{
				tmpAlignment.Crossing = true;
				let tmpSharedWith = (pInputs.DestContractProjects || []).filter((pProject) => Number(pProject.IDProject) !== Number(pInputs.DestIDProject));
				if (!tmpContract.GUIDContract)
				{
					tmpAlignment.Warnings.push(`Contract ${tmpContract.Name || tmpContract.IDContract} has no stable identifier (GUID), so its specification sets cannot be updated by this copy.`);
				}
				else
				{
					tmpAlignment.Contract = (
						{
							IDContract: tmpContract.IDContract,
							GUIDContract: tmpContract.GUIDContract,
							Name: tmpContract.Name || '',
							Before: tmpBefore,
							After: { ItemIDTestSpecificationSet: tmpItemSetID, MaterialIDTestSpecificationSet: tmpMaterialSetID },
							SharedWithProjects: tmpSharedWith
						});
					if (tmpSharedWith.length > 0)
					{
						tmpAlignment.Warnings.push(`Contract ${tmpContract.Name || tmpContract.IDContract} is shared with ${tmpSharedWith.length} other project(s) (${tmpSharedWith.map((pProject) => pProject.IDProject).join(', ')}); changing its specification sets affects them too.`);
					}
				}
			}
		}

		let tmpLineItems = pInputs.DestLineItems || [];
		for (let i = 0; i < tmpLineItems.length; i++)
		{
			let tmpLineItem = tmpLineItems[i];
			let tmpPayItem = (tmpLineItem.IDPayItem > 0) ? pInputs.DestPayItemsByID[tmpLineItem.IDPayItem] : null;
			if (!tmpPayItem)
			{
				tmpAlignment.WithoutPayItemCount++;
				continue;
			}
			let tmpFromSetID = Number(tmpPayItem.IDTestSpecificationSet) || 0;
			if (tmpFromSetID === tmpItemSetID)
			{
				tmpAlignment.AlreadyAlignedCount++;
				continue;
			}

			tmpAlignment.Crossing = true;
			let tmpItemCode = tmpPayItem.ItemCode || tmpLineItem.ItemCode || '';
			let tmpCandidates = (tmpItemCode && pInputs.TargetPayItemsByItemCode[tmpItemCode]) || [];
			let tmpReason = '';
			if (tmpCandidates.length === 0)
			{
				tmpReason = `No pay item with item code ${tmpItemCode} in specification set ${tmpItemSetID}.`;
			}
			else if (tmpCandidates.length > 1)
			{
				tmpReason = `${tmpCandidates.length} pay items share item code ${tmpItemCode} in specification set ${tmpItemSetID}.`;
			}
			else if (!tmpLineItem.GUIDLineItem)
			{
				tmpReason = 'Line item has no stable identifier (GUID), so it cannot be updated.';
			}
			if (tmpReason)
			{
				tmpAlignment.Unmapped.push({ IDLineItem: tmpLineItem.IDLineItem, ItemCode: tmpLineItem.ItemCode || '', Name: tmpLineItem.Name || '', Reason: tmpReason });
				continue;
			}

			tmpAlignment.LineItemRemaps.push(
				{
					IDLineItem: tmpLineItem.IDLineItem,
					GUIDLineItem: tmpLineItem.GUIDLineItem,
					ItemCode: tmpItemCode,
					Name: tmpLineItem.Name || '',
					FromIDPayItem: tmpLineItem.IDPayItem,
					FromIDTestSpecificationSet: tmpFromSetID,
					ToIDPayItem: tmpCandidates[0].IDPayItem
				});
		}

		if (tmpAlignment.Unmapped.length > 0)
		{
			tmpAlignment.Warnings.push(`${tmpAlignment.Unmapped.length} destination line item(s) have no matching pay item in specification set ${tmpItemSetID} and will keep their current pay item.`);
		}

		return tmpAlignment;
	}

	/**
	 * Settle what a copy applies from an alignment plan. With the flip on the plan is kept as-is; with it off the
	 * crossing is still reported, but no Contract or LineItem change is planned.
	 *
	 * @param {SpecificationSetAlignment} pAlignment - The plan from `planSpecificationSetAlignment`.
	 * @param {boolean} pFlipTargetSpecificationSet - Whether the copy moves the destination onto the source's sets.
	 * @return {SpecificationSetAlignment} The alignment the copy applies, with `Flip` set.
	 */
	resolveSpecificationSetAlignment(pAlignment, pFlipTargetSpecificationSet)
	{
		if (pFlipTargetSpecificationSet)
		{
			return Object.assign({}, pAlignment, { Flip: true });
		}
		return Object.assign({}, pAlignment, { Flip: false, Contract: null, LineItemRemaps: [], Unmapped: [], Warnings: [] });
	}

	/**
	 * Turn an alignment plan into comprehension records. Both entities update existing rows, so
	 * they carry `_GUID<Entity>` (used verbatim) rather than `GUID<Entity>` (which gets prefixed
	 * and would insert a new row).
	 *
	 * @param {SpecificationSetAlignment} pAlignment - The plan.
	 * @return {{Contract?: Record<string, Record<string, any>>, LineItem?: Record<string, Record<string, any>>}} Comprehension entities; empty when nothing changes.
	 */
	buildSpecificationSetRealignmentRecords(pAlignment)
	{
		let tmpRecords = {};
		if (pAlignment.Contract)
		{
			tmpRecords.Contract = {};
			tmpRecords.Contract[pAlignment.Contract.GUIDContract] = (
				{
					_GUIDContract: pAlignment.Contract.GUIDContract,
					ItemIDTestSpecificationSet: pAlignment.Contract.After.ItemIDTestSpecificationSet,
					MaterialIDTestSpecificationSet: pAlignment.Contract.After.MaterialIDTestSpecificationSet
				});
		}
		if (pAlignment.LineItemRemaps.length > 0)
		{
			tmpRecords.LineItem = {};
			for (let i = 0; i < pAlignment.LineItemRemaps.length; i++)
			{
				let tmpRemap = pAlignment.LineItemRemaps[i];
				tmpRecords.LineItem[tmpRemap.GUIDLineItem] = { _GUIDLineItem: tmpRemap.GUIDLineItem, IDPayItem: tmpRemap.ToIDPayItem };
			}
		}
		return tmpRecords;
	}

	/**
	 * @typedef {object} SamplePlanCopyOptions
	 * @property {boolean} [FlipTargetSpecificationSet] - Also move the destination onto the source's specification sets
	 *   (Contract set columns and LineItem.IDPayItem). Off by default: the destination keeps its sets and only plan rows are copied.
	 */

	/**
	 * Build a sample plan comprehension by matching source project line items
	 * to destination project line items by ItemCode. The copy only adds and updates rows; it never removes any.
	 *
	 * @param {number} pSourceIDProject - Source (master) project IDProject.
	 * @param {number} pDestIDProject - Destination project IDProject.
	 * @param {SamplePlanCopyOptions|Function} pOptions - Copy options; may be omitted, in which case this is the callback.
	 * @param {Function} [fCallback] - Callback: (error, result) where result is { comprehension, reportData, warnings }.
	 */
	buildComprehension(pSourceIDProject, pDestIDProject, pOptions, fCallback)
	{
		if (typeof pOptions === 'function')
		{
			return this.buildComprehension(pSourceIDProject, pDestIDProject, {}, pOptions);
		}
		let tmpFlipTargetSpecificationSet = !!(pOptions && pOptions.FlipTargetSpecificationSet);

		let tmpSourceProject = {};
		let tmpDestProject = {};
		let tmpSourceContext = null;
		let tmpDestContext = null;
		let tmpPayItemsByID = {};
		let tmpSourceSets = null;
		let tmpDestSets = null;
		let tmpTargetPayItemsByItemCode = {};
		let tmpSetNames = {};
		let tmpSourceLineItems = [];
		let tmpSourceLineItemCount = 0;
		let tmpSourcePLIMTTotal = 0;
		let tmpSourcePLIMTs = [];
		let tmpSourceMLIJs = [];
		let tmpDestLineItems = [];
		let tmpDestExistingPLIMTCount = 0;
		let tmpDestExistingMLIJCount = 0;
		let tmpWarnings = [];

		this.log.info(`Sample Plan Comprehension Builder`);
		this.log.info(`  Source IDProject:      ${pSourceIDProject}`);
		this.log.info(`  Destination IDProject: ${pDestIDProject}`);
		this.log.info(`  Flip target specification set: ${tmpFlipTargetSpecificationSet ? 'yes' : 'no'}`);

		this.fable.Utility.waterfall(
			[
				// Stage 1: Fetch both projects and their contracts
				(fStageComplete) =>
				{
					this.log.info(`Fetching project records...`);
					this.getProjectContext(pSourceIDProject,
						(pError, pSourceContext) =>
						{
							if (pError)
							{
								this.log.error(`Error fetching source project`, pError);
								return fStageComplete(pError);
							}
							tmpSourceContext = pSourceContext;
							tmpSourceProject = pSourceContext.Project;
							this.log.info(`  Source:      [${tmpSourceProject.IDProject}] ${tmpSourceProject.Name || '(unnamed)'}`);

							this.getProjectContext(pDestIDProject,
								(pError2, pDestContext) =>
								{
									if (pError2)
									{
										this.log.error(`Error fetching destination project`, pError2);
										return fStageComplete(pError2);
									}
									tmpDestContext = pDestContext;
									tmpDestProject = pDestContext.Project;
									this.log.info(`  Destination: [${tmpDestProject.IDProject}] ${tmpDestProject.Name || '(unnamed)'}`);
									let tmpDestContract = tmpDestContext.Contract;
									if (tmpDestContract)
									{
										this.log.info(`  Destination contract [${tmpDestContract.IDContract}] item set ${tmpDestContract.ItemIDTestSpecificationSet}, material set ${tmpDestContract.MaterialIDTestSpecificationSet}, on ${tmpDestContext.ContractProjects.length} project(s)`);
									}
									return fStageComplete();
								});
						});
				},

				// Stage 2: Count all source line items (regardless of matching)
				(fStageComplete) =>
				{
					this.log.info(`Counting source project line items...`);
					this.restClient.getEntitySetCount('LineItem',
						`FBV~IDProject~EQ~${pSourceIDProject}~FBV~Deleted~EQ~0`,
						(pError, pResult) =>
						{
							if (pError)
							{
								this.log.error(`Error counting source line items`, pError);
								return fStageComplete(pError);
							}
							tmpSourceLineItemCount = (pResult && pResult.Count) ? pResult.Count : 0;
							this.log.info(`  Source project has ${tmpSourceLineItemCount} total line items.`);
							this.restClient.getEntitySetCount('ProjectLineItemMaterialTest',
								`FBV~IDProject~EQ~${pSourceIDProject}~FBV~Deleted~EQ~0`,
								(pPLIMTError, pPLIMTResult) =>
								{
									if (pPLIMTError)
									{
										this.log.error(`Error counting source PLIMTs`, pPLIMTError);
										return fStageComplete(pPLIMTError);
									}
									tmpSourcePLIMTTotal = (pPLIMTResult && pPLIMTResult.Count) ? pPLIMTResult.Count : 0;
									this.log.info(`  Source project has ${tmpSourcePLIMTTotal} sample plan tests.`);
									return fStageComplete();
								});
						});
				},

				// Stage 3: Fetch destination line items
				(fStageComplete) =>
				{
					this.log.info(`Fetching destination project line items...`);
					this.restClient.getEntitySet('LineItem',
						`FBV~IDProject~EQ~${pDestIDProject}~FBV~Deleted~EQ~0`,
						(pError, pRecords) =>
						{
							if (pError)
							{
								this.log.error(`Error fetching destination line items`, pError);
							}
							else
							{
								tmpDestLineItems = pRecords;
								this.log.info(`  Found ${tmpDestLineItems.length} destination line items.`);
							}
							return fStageComplete(pError);
						}, 0, 100000);
				},

				// Stage 4: Fetch matching source line items by ItemCode
				(fStageComplete) =>
				{
					this.log.info(`Fetching matching source project line items...`);
					let tmpDestItemCodes = {};
					for (let i = 0; i < tmpDestLineItems.length; i++)
					{
						if (tmpDestLineItems[i].ItemCode)
						{
							tmpDestItemCodes[tmpDestLineItems[i].ItemCode] = true;
						}
					}
					let tmpUniqueItemCodes = Object.keys(tmpDestItemCodes);
					this.log.info(`  Destination has ${tmpUniqueItemCodes.length} unique ItemCodes.`);

					if (tmpUniqueItemCodes.length === 0)
					{
						this.log.warn(`  No ItemCodes found in destination — nothing to match.`);
						return fStageComplete();
					}

					this.getEntitySetBatched('LineItem', 'ItemCode', tmpUniqueItemCodes,
						`FBV~IDProject~EQ~${pSourceIDProject}`,
						(pError, pRecords) =>
						{
							if (pError)
							{
								this.log.error(`Error fetching source line items`, pError);
							}
							else
							{
								tmpSourceLineItems = pRecords;
								this.log.info(`  Found ${tmpSourceLineItems.length} matching source line items.`);
							}
							return fStageComplete(pError);
						});
				},

				// Stage 5: Fetch source PLIMTs
				(fStageComplete) =>
				{
					let tmpSourceLineItemIDs = tmpSourceLineItems.map((pLI) => pLI.IDLineItem);

					if (tmpSourceLineItemIDs.length === 0)
					{
						this.log.warn(`No matching source line items — skipping PLIMT fetch.`);
						return fStageComplete();
					}

					this.log.info(`Fetching source ProjectLineItemMaterialTests for ${tmpSourceLineItemIDs.length} line items...`);
					this.getEntitySetBatched('ProjectLineItemMaterialTest', 'IDLineItem', tmpSourceLineItemIDs, null,
						(pError, pRecords) =>
						{
							if (pError)
							{
								this.log.error(`Error fetching source PLIMTs`, pError);
							}
							else
							{
								tmpSourcePLIMTs = pRecords;
								this.log.info(`  Found ${tmpSourcePLIMTs.length} source ProjectLineItemMaterialTests.`);
							}
							return fStageComplete(pError);
						});
				},

				// Stage 6: Fetch source MaterialLineItemJoins
				(fStageComplete) =>
				{
					let tmpSourceLineItemIDs = tmpSourceLineItems.map((pLI) => pLI.IDLineItem);

					if (tmpSourceLineItemIDs.length === 0)
					{
						this.log.warn(`No matching source line items — skipping MLJ fetch.`);
						return fStageComplete();
					}

					this.log.info(`Fetching source MaterialLineItemJoins...`);
					this.getEntitySetBatched('MaterialLineItemJoin', 'IDLineItem', tmpSourceLineItemIDs, null,
						(pError, pRecords) =>
						{
							if (pError)
							{
								this.log.error(`Error fetching source MLIJs`, pError);
							}
							else
							{
								tmpSourceMLIJs = pRecords;
								this.log.info(`  Found ${tmpSourceMLIJs.length} source MaterialLineItemJoins.`);
							}
							return fStageComplete(pError);
						});
				},

				// Stage 7: Check for existing sample plan data in destination
				(fStageComplete) =>
				{
					this.log.info(`Checking for existing sample plan data in destination...`);
					this.restClient.getEntitySetCount('ProjectLineItemMaterialTest',
						`FBV~IDProject~EQ~${pDestIDProject}~FBV~Deleted~EQ~0`,
						(pError, pResult) =>
						{
							if (pError)
							{
								this.log.error(`Error checking existing PLIMTs`, pError);
								return fStageComplete(pError);
							}

							let tmpExistingCount = (pResult && pResult.Count) ? pResult.Count : 0;
							tmpDestExistingPLIMTCount = tmpExistingCount;
							if (tmpExistingCount > 0)
							{
								let tmpMsg = `Destination project already has ${tmpExistingCount} sample plan tests. The copy adds to them and removes none, so they may duplicate the copied tests.`;
								this.log.warn(tmpMsg);
								tmpWarnings.push(tmpMsg);
							}
							else
							{
								this.log.info(`  No existing sample plan data in destination.`);
							}

							let tmpDestLineItemIDs = tmpDestLineItems.map((pLI) => pLI.IDLineItem);
							this.getEntitySetCountBatched('MaterialLineItemJoin', 'IDLineItem', tmpDestLineItemIDs, null,
								(pMLIJError, pMLIJCount) =>
								{
									if (pMLIJError)
									{
										let tmpMsg = `Could not check whether the destination line items already have materials assigned.`;
										this.log.warn(tmpMsg, pMLIJError);
										tmpWarnings.push(tmpMsg);
									}
									else if (pMLIJCount > 0)
									{
										tmpDestExistingMLIJCount = pMLIJCount;
										let tmpMsg = `Destination line items already have ${pMLIJCount} materials assigned.`;
										this.log.warn(tmpMsg);
										tmpWarnings.push(tmpMsg);
									}
									return fStageComplete();
								});
						});
				},

				// Stage 8: Resolve each project's specification sets
				(fStageComplete) =>
				{
					this.log.info(`Resolving specification sets...`);
					this.resolveProjectSpecificationSets(tmpSourceContext, SPECIFICATION_SET_LINE_ITEM_SAMPLE,
						(pSourceError, pSourceSets) =>
						{
							if (pSourceError)
							{
								this.log.error(`Error resolving the source project's specification sets`, pSourceError);
								return fStageComplete(pSourceError);
							}
							tmpSourceSets = pSourceSets;
							this.getPayItemsForLineItems(tmpDestLineItems,
								(pError, pPayItemsByID) =>
								{
									if (pError)
									{
										this.log.error(`Error fetching destination line item pay items`, pError);
										return fStageComplete(pError);
									}
									tmpPayItemsByID = pPayItemsByID;
									tmpDestSets = this.summarizeProjectSpecificationSets(tmpDestContext, tmpDestLineItems, tmpPayItemsByID);
									this.log.info(`  Source:      item set ${tmpSourceSets.ItemIDTestSpecificationSet}, material set ${tmpSourceSets.MaterialIDTestSpecificationSet} (from ${tmpSourceSets.Basis})`);
									this.log.info(`  Destination: item set ${tmpDestSets.ItemIDTestSpecificationSet}, material set ${tmpDestSets.MaterialIDTestSpecificationSet} (from ${tmpDestSets.Basis})`);
									if (tmpFlipTargetSpecificationSet && tmpSourceSets.PayItemSetMixed && (tmpSourceSets.Basis !== 'Contract'))
									{
										let tmpSetCounts = Object.keys(tmpSourceSets.PayItemSetDistribution).map((pID) => `set ${pID}: ${tmpSourceSets.PayItemSetDistribution[pID]}`).join(', ');
										let tmpMsg = `Source line items use pay items from more than one specification set (${tmpSetCounts}), so the destination will not be moved onto the source's sets.`;
										this.log.warn(tmpMsg);
										tmpWarnings.push(tmpMsg);
									}
									return fStageComplete();
								});
						});
				},

				// Stage 9: Fetch target-set pay items for destination line items on another set
				(fStageComplete) =>
				{
					let tmpTargetSetID = tmpSourceSets.ItemIDTestSpecificationSet;
					if (!tmpFlipTargetSpecificationSet || !(tmpTargetSetID > 0))
					{
						return fStageComplete();
					}

					let tmpItemCodes = {};
					for (let i = 0; i < tmpDestLineItems.length; i++)
					{
						let tmpPayItem = tmpPayItemsByID[tmpDestLineItems[i].IDPayItem];
						let tmpItemCode = tmpPayItem ? (tmpPayItem.ItemCode || tmpDestLineItems[i].ItemCode) : null;
						if (tmpItemCode && ((Number(tmpPayItem.IDTestSpecificationSet) || 0) !== tmpTargetSetID))
						{
							tmpItemCodes[tmpItemCode] = true;
						}
					}
					let tmpUniqueItemCodes = Object.keys(tmpItemCodes);
					if (tmpUniqueItemCodes.length === 0)
					{
						return fStageComplete();
					}

					this.log.info(`Fetching set ${tmpTargetSetID} pay items for ${tmpUniqueItemCodes.length} destination ItemCodes on another set...`);
					let tmpExtraFilter = `FBV~IDTestSpecificationSet~EQ~${tmpTargetSetID}`;
					if (tmpDestProject.IDCustomer > 0)
					{
						tmpExtraFilter += `~FBV~IDCustomer~EQ~${tmpDestProject.IDCustomer}`;
					}
					this.getEntitySetBatched('PayItem', 'ItemCode', tmpUniqueItemCodes, tmpExtraFilter,
						(pError, pPayItems) =>
						{
							if (pError)
							{
								this.log.error(`Error fetching target-set pay items`, pError);
								return fStageComplete(pError);
							}
							for (let i = 0; i < pPayItems.length; i++)
							{
								let tmpItemCode = pPayItems[i].ItemCode;
								if (!tmpTargetPayItemsByItemCode[tmpItemCode])
								{
									tmpTargetPayItemsByItemCode[tmpItemCode] = [];
								}
								tmpTargetPayItemsByItemCode[tmpItemCode].push(pPayItems[i]);
							}
							return fStageComplete();
						});
				},

				// Stage 10: Name the specification sets involved
				(fStageComplete) =>
				{
					let tmpSetIDs = [
						tmpSourceSets.ItemIDTestSpecificationSet,
						tmpSourceSets.MaterialIDTestSpecificationSet,
						tmpDestSets.ItemIDTestSpecificationSet,
						tmpDestSets.MaterialIDTestSpecificationSet
					].concat(Object.keys(tmpDestSets.PayItemSetDistribution).map((pID) => parseInt(pID, 10)));
					this.getSpecificationSetNames(tmpSetIDs,
						(pError, pNames) =>
						{
							if (pError)
							{
								this.log.warn(`Could not fetch specification set names; IDs will be shown instead.`, pError);
							}
							tmpSetNames = pNames || {};
							return fStageComplete();
						});
				},

				// Stage 11: Build the comprehension and report data
				(fStageComplete) =>
				{
					this.log.info(`Building comprehension...`);
					this.log.info(`  Indexing source data...`);
					let tmpSourceIndex = this.indexLineItemsByItemCode(tmpSourceLineItems);
					let tmpSourceLineItemsByItemCode = tmpSourceIndex.ByItemCode;
					if (tmpSourceIndex.Warning)
					{
						this.log.warn(tmpSourceIndex.Warning);
						tmpWarnings.push(tmpSourceIndex.Warning);
					}

					let tmpSourcePLIMTsByIDLineItem = {};
					for (let i = 0; i < tmpSourcePLIMTs.length; i++)
					{
						let tmpPLIMT = tmpSourcePLIMTs[i];
						if (!tmpSourcePLIMTsByIDLineItem[tmpPLIMT.IDLineItem])
						{
							tmpSourcePLIMTsByIDLineItem[tmpPLIMT.IDLineItem] = [];
						}
						tmpSourcePLIMTsByIDLineItem[tmpPLIMT.IDLineItem].push(tmpPLIMT);
					}

					let tmpSourceMLIJsByIDLineItem = {};
					for (let i = 0; i < tmpSourceMLIJs.length; i++)
					{
						let tmpMLIJ = tmpSourceMLIJs[i];
						if (!tmpSourceMLIJsByIDLineItem[tmpMLIJ.IDLineItem])
						{
							tmpSourceMLIJsByIDLineItem[tmpMLIJ.IDLineItem] = [];
						}
						tmpSourceMLIJsByIDLineItem[tmpMLIJ.IDLineItem].push(tmpMLIJ);
					}

					let tmpAlignment = this.resolveSpecificationSetAlignment(this.planSpecificationSetAlignment(
						{
							ItemIDTestSpecificationSet: tmpSourceSets.ItemIDTestSpecificationSet,
							MaterialIDTestSpecificationSet: tmpSourceSets.MaterialIDTestSpecificationSet,
							DestIDProject: pDestIDProject,
							DestContract: tmpDestContext.Contract,
							DestContractProjects: tmpDestContext.ContractProjects,
							DestLineItems: tmpDestLineItems,
							DestPayItemsByID: tmpPayItemsByID,
							TargetPayItemsByItemCode: tmpTargetPayItemsByItemCode
						}), tmpFlipTargetSpecificationSet);
					if (tmpAlignment.Crossing && tmpAlignment.Flip)
					{
						this.log.info(`  Specification set crossing: ${tmpAlignment.Contract ? 1 : 0} Contract update, ${tmpAlignment.LineItemRemaps.length} LineItem pay item remaps, ${tmpAlignment.Unmapped.length} unmapped, ${tmpAlignment.AlreadyAlignedCount} already aligned.`);
					}
					else if (tmpAlignment.Crossing)
					{
						tmpAlignment.Warnings.push(`The source is on specification set ${tmpSourceSets.ItemIDTestSpecificationSet} and the destination on ${tmpDestSets.ItemIDTestSpecificationSet || 'none'}. The destination keeps its specification sets and line item pay items; sample plan tests are copied onto its line items by item code.`);
					}
					for (let i = 0; i < tmpAlignment.Warnings.length; i++)
					{
						this.log.warn(tmpAlignment.Warnings[i]);
						tmpWarnings.push(tmpAlignment.Warnings[i]);
					}

					let tmpComprehension = Object.assign(this.buildSpecificationSetRealignmentRecords(tmpAlignment),
						{
							ProjectLineItemMaterialTest: {},
							MaterialLineItemJoin: {}
						});

					let tmpMatchedCount = 0;
					let tmpUnmatchedCount = 0;
					let tmpPLIMTRowCount = 0;
					let tmpMLIJCount = 0;
					let tmpSeenMLIJKeys = {};
					let tmpUsedSourceLineItemIDs = {};
					let tmpReportLineItems = [];

					this.log.info(`  Indexed ${Object.keys(tmpSourceLineItemsByItemCode).length} source ItemCodes, ${Object.keys(tmpSourcePLIMTsByIDLineItem).length} PLIMT groups, ${Object.keys(tmpSourceMLIJsByIDLineItem).length} MLJ groups.`);
					this.log.info(`Processing ${tmpDestLineItems.length} destination line items...`);

					for (let i = 0; i < tmpDestLineItems.length; i++)
					{
						let tmpDestLI = tmpDestLineItems[i];
						let tmpSourceLI = tmpSourceLineItemsByItemCode[tmpDestLI.ItemCode];

						if (!tmpSourceLI)
						{
							tmpUnmatchedCount++;
							tmpReportLineItems.push({
								itemCode: tmpDestLI.ItemCode || '',
								name: tmpDestLI.Name || '',
								units: tmpDestLI.Units || '',
								contractQuantity: tmpDestLI.ContractQuantity || 0,
								matched: false,
								plimtCount: 0,
								mljCount: 0,
								tests: []
							});
							continue;
						}

						tmpMatchedCount++;
						tmpUsedSourceLineItemIDs[tmpSourceLI.IDLineItem] = true;
						let tmpSourcePLIMTsForLI = tmpSourcePLIMTsByIDLineItem[tmpSourceLI.IDLineItem] || [];
						let tmpSourceMLIJsForLI = tmpSourceMLIJsByIDLineItem[tmpSourceLI.IDLineItem] || [];
						this.log.info(`  [${tmpMatchedCount}] ${tmpDestLI.ItemCode} (IDLineItem ${tmpDestLI.IDLineItem}) -> ${tmpSourcePLIMTsForLI.length} PLIMTs, ${tmpSourceMLIJsForLI.length} MLIJs`);

						let tmpTestDetails = [];

						// Copy PLIMTs for this line item
						for (let j = 0; j < tmpSourcePLIMTsForLI.length; j++)
						{
							let tmpSrc = tmpSourcePLIMTsForLI[j];
							let tmpCompositeKey = `${pDestIDProject}|${tmpDestLI.IDLineItem}|${tmpSrc.IDMaterial}|${tmpSrc.IDTest}|${tmpSrc.SampleType}`;
							let tmpGUID = this.generateGUID('MSP-PLIMT', tmpCompositeKey);

							tmpComprehension.ProjectLineItemMaterialTest[tmpGUID] = {
								GUIDProjectLineItemMaterialTest: tmpGUID,
								IDProject: pDestIDProject,
								IDLineItem: tmpDestLI.IDLineItem,
								IDMaterial: tmpSrc.IDMaterial,
								IDTest: tmpSrc.IDTest,
								IDLab: tmpSrc.IDLab || 0,
								Intent: tmpSrc.Intent || '',
								SampleType: tmpSrc.SampleType || '',
								SampleSize: tmpSrc.SampleSize || 0,
								SampleUnits: tmpSrc.SampleUnits || '',
								Rate: tmpSrc.Rate || 0,
								Frequency: tmpSrc.Frequency || 0,
								Units: tmpSrc.Units || '',
								TestDescription: tmpSrc.TestDescription || '',
								SampleDescription: tmpSrc.SampleDescription || '',
								SamplingInstructions: tmpSrc.SamplingInstructions || '',
								SamplingRequirements: tmpSrc.SamplingRequirements || '',
								SamplingPeriod: tmpSrc.SamplingPeriod || '',
								Remarks: tmpSrc.Remarks || '',
								ConversionFactor: tmpSrc.ConversionFactor || 1,
								CertificateRequired: tmpSrc.CertificateRequired || 0,
								TestsNotRequired: tmpSrc.TestsNotRequired || 0,
								ErrorOmission: tmpSrc.ErrorOmission || 0,
								ErrorOmissionDetail: tmpSrc.ErrorOmissionDetail || '',
								ManualPlannedSampleCount: -1,
								ManualRequiredSampleCount: -1,
								ComputedPlannedSampleCount: 0,
								ComputedRequiredSampleCount: 0,
								CreatedSampleCount: 0,
								PassingSampleCount: 0,
								FailingSampleCount: 0,
								Deleted: 0
							};
							tmpPLIMTRowCount++;

							tmpTestDetails.push({
								testDescription: tmpSrc.TestDescription || '',
								sampleType: tmpSrc.SampleType || '',
								intent: tmpSrc.Intent || '',
								rate: tmpSrc.Rate || 0,
								frequency: tmpSrc.Frequency || 0,
								units: tmpSrc.Units || '',
								sampleSize: tmpSrc.SampleSize || 0,
								sampleUnits: tmpSrc.SampleUnits || '',
								remarks: tmpSrc.Remarks || ''
							});
						}

						// Copy MLIJs for this line item (deduplicate)
						let tmpLIMljCount = 0;
						for (let j = 0; j < tmpSourceMLIJsForLI.length; j++)
						{
							let tmpSrc = tmpSourceMLIJsForLI[j];
							let tmpMLIJKey = `${tmpDestLI.IDLineItem}|${tmpSrc.IDMaterial}`;

							if (tmpSeenMLIJKeys[tmpMLIJKey]) continue;
							tmpSeenMLIJKeys[tmpMLIJKey] = true;

							let tmpGUID = this.generateGUID('MSP-MLJ', tmpMLIJKey);

							tmpComprehension.MaterialLineItemJoin[tmpGUID] = {
								GUIDMaterialLineItemJoin: tmpGUID,
								IDLineItem: tmpDestLI.IDLineItem,
								IDMaterial: tmpSrc.IDMaterial,
								ConversionFactor: tmpSrc.ConversionFactor || 1,
								ApprovedSourceRequired: tmpSrc.ApprovedSourceRequired || 0,
								SpecialInstructions: tmpSrc.SpecialInstructions || '',
								Units: tmpSrc.Units || '',
								SampleRate: tmpSrc.SampleRate || 0,
								SampleFrequency: tmpSrc.SampleFrequency || 0,
								RequiredSampleCount: tmpSrc.RequiredSampleCount || 0,
								Quantity: tmpSrc.Quantity || 0,
								Deleted: 0
							};
							tmpMLIJCount++;
							tmpLIMljCount++;
						}

						tmpReportLineItems.push({
							itemCode: tmpDestLI.ItemCode || '',
							name: tmpDestLI.Name || '',
							units: tmpDestLI.Units || '',
							contractQuantity: tmpDestLI.ContractQuantity || 0,
							matched: true,
							plimtCount: tmpSourcePLIMTsForLI.length,
							mljCount: tmpLIMljCount,
							tests: tmpTestDetails
						});
					}

					let tmpPLIMTCount = Object.keys(tmpComprehension.ProjectLineItemMaterialTest).length;
					let tmpMappedSourcePLIMTCount = 0;
					for (let i = 0; i < tmpSourcePLIMTs.length; i++)
					{
						if (tmpUsedSourceLineItemIDs[tmpSourcePLIMTs[i].IDLineItem])
						{
							tmpMappedSourcePLIMTCount++;
						}
					}
					let tmpUnmappedSourcePLIMTCount = Math.max(0, tmpSourcePLIMTTotal - tmpMappedSourcePLIMTCount);
					let tmpCollapsedPLIMTCount = tmpPLIMTRowCount - tmpPLIMTCount;

					this.log.info(`=== Comprehension Built ===`);
					this.log.info(`  Source Line Items: ${tmpSourceLineItems.length}`);
					this.log.info(`  Destination Line Items: ${tmpDestLineItems.length}`);
					this.log.info(`  Matched Line Items: ${tmpMatchedCount}`);
					this.log.info(`  Unmatched Destination Line Items: ${tmpUnmatchedCount}`);
					this.log.info(`  Source ProjectLineItemMaterialTests: ${tmpSourcePLIMTTotal} (${tmpMappedSourcePLIMTCount} on matched line items, ${tmpUnmappedSourcePLIMTCount} unmapped)`);
					this.log.info(`  ProjectLineItemMaterialTest Records: ${tmpPLIMTCount}`);
					this.log.info(`  MaterialLineItemJoin Records: ${tmpMLIJCount}`);

					if (tmpUnmatchedCount > 0)
					{
						let tmpMsg = `${tmpUnmatchedCount} destination line items have no line item with the same item code in the source project.`;
						this.log.warn(tmpMsg);
						tmpWarnings.push(tmpMsg);
					}
					if (tmpUnmappedSourcePLIMTCount > 0)
					{
						let tmpMsg = `${tmpUnmappedSourcePLIMTCount} of the source's ${tmpSourcePLIMTTotal} sample plan tests are on line items whose item code the destination does not have (or repeats of an item code), so they are not copied.`;
						this.log.warn(tmpMsg);
						tmpWarnings.push(tmpMsg);
					}
					if (tmpCollapsedPLIMTCount > 0)
					{
						let tmpMsg = `${tmpCollapsedPLIMTCount} source sample plan tests repeat the same material, test and sample type on one line item, so each repeat is copied once.`;
						this.log.warn(tmpMsg);
						tmpWarnings.push(tmpMsg);
					}

					let tmpReportData = {
						sourceProject: tmpSourceProject,
						destProject: tmpDestProject,
						generatedDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
						stats: {
							sourceLineItemCount: tmpSourceLineItemCount,
							destLineItemCount: tmpDestLineItems.length,
							matched: tmpMatchedCount,
							unmatched: tmpUnmatchedCount,
							plimtCount: tmpPLIMTCount,
							mljCount: tmpMLIJCount,
							sourcePlimtCount: tmpSourcePLIMTTotal,
							mappedSourcePlimtCount: tmpMappedSourcePLIMTCount,
							unmappedSourcePlimtCount: tmpUnmappedSourcePLIMTCount,
							collapsedPlimtCount: tmpCollapsedPLIMTCount,
							destExistingPlimtCount: tmpDestExistingPLIMTCount,
							destExistingMljCount: tmpDestExistingMLIJCount,
							contractUpdateCount: tmpAlignment.Contract ? 1 : 0,
							lineItemRemapCount: tmpAlignment.LineItemRemaps.length,
							lineItemUnmappedCount: tmpAlignment.Unmapped.length
						},
						options: { FlipTargetSpecificationSet: tmpFlipTargetSpecificationSet },
						lineItems: tmpReportLineItems,
						specificationSets: {
							Source: tmpSourceSets,
							Dest: tmpDestSets,
							Names: tmpSetNames,
							Alignment: tmpAlignment
						}
					};

					return fStageComplete(null, { comprehension: tmpComprehension, reportData: tmpReportData, warnings: tmpWarnings });
				}
			],
			(pError, pResult) =>
			{
				if (pError)
				{
					this.log.error(`Sample plan comprehension build failed.`, pError);
				}
				return fCallback(pError, pResult);
			});
	}

	/**
	 * Look up a Project record by GUIDProject. Returns null (not an error) when not found,
	 * so the caller can use "missing" as a natural skip case.
	 *
	 * @param {string} pGUIDProject - The GUID to look up.
	 * @param {Function} fCallback - Callback: (error, project or null).
	 */
	lookupProjectByGUID(pGUIDProject, fCallback)
	{
		if (!pGUIDProject)
		{
			return fCallback(null, null);
		}
		let tmpFilter = `FBV~GUIDProject~EQ~${encodeURIComponent(pGUIDProject)}`;
		this.restClient.getEntitySet('Project', tmpFilter,
			(pError, pRecords) =>
			{
				if (pError)
				{
					return fCallback(pError);
				}
				if (!pRecords || pRecords.length === 0)
				{
					return fCallback(null, null);
				}
				return fCallback(null, pRecords[0]);
			}, 0, 1);
	}

	/**
	 * Sibling of `buildComprehension` for the case where the destination project (and its
	 * LineItems) does NOT yet exist on the server — typically because it's about to be
	 * created in the same comprehension push the caller is assembling.
	 *
	 * Differences from `buildComprehension`:
	 *   - Source project is looked up by `GUIDProject` instead of an integer `IDProject`.
	 *   - Destination LineItems are passed in directly rather than fetched via REST.
	 *   - Output PLIMT / MLJ records use `GUIDProject` / `GUIDLineItem` FK refs (not integer IDs)
	 *     so the integration adapter resolves them at push time against the same comprehension.
	 *
	 * Returns `null` for `result` when the source GUID does not resolve, so callers can use
	 * "no master sample plan for this year, skip" as a natural pattern.
	 *
	 * @param {object} pOptions
	 * @param {string} pOptions.SourceGUIDProject - GUID of the source / master sample plan project (e.g. `HLICLI-P-L26-ProtoMSP`).
	 * @param {string} pOptions.DestGUIDProject - GUID of the destination project (used as the FK ref in output records).
	 * @param {Array<{GUIDLineItem: string, ItemCode: string}>} pOptions.DestLineItems - Destination line items from the in-flight comprehension. Each must carry at least `GUIDLineItem` and `ItemCode`.
	 * @param {string} [pOptions.GUIDPrefixPLIMT] - Prefix for generated PLIMT GUIDs. Defaults to `MSP-PLIMT`.
	 * @param {string} [pOptions.GUIDPrefixMLJ] - Prefix for generated MLJ GUIDs. Defaults to `MSP-MLJ`.
	 * @param {Function} fCallback - Callback: (error, result) where result is { comprehension, reportData, warnings } or null when the source project is not found.
	 */
	buildComprehensionFromLineItems(pOptions, fCallback)
	{
		let tmpOptions = pOptions || {};
		let tmpSourceGUIDProject = tmpOptions.SourceGUIDProject;
		let tmpDestGUIDProject = tmpOptions.DestGUIDProject;
		let tmpDestLineItems = Array.isArray(tmpOptions.DestLineItems) ? tmpOptions.DestLineItems : [];
		let tmpGUIDPrefixPLIMT = tmpOptions.GUIDPrefixPLIMT || 'MSP-PLIMT';
		let tmpGUIDPrefixMLJ = tmpOptions.GUIDPrefixMLJ || 'MSP-MLJ';

		if (!tmpSourceGUIDProject)
		{
			return fCallback(new Error('SourceGUIDProject is required.'));
		}
		if (!tmpDestGUIDProject)
		{
			return fCallback(new Error('DestGUIDProject is required.'));
		}

		let tmpSourceProject = null;
		let tmpSourceSets = null;
		let tmpSourceLineItems = [];
		let tmpSourceLineItemCount = 0;
		let tmpSourcePLIMTs = [];
		let tmpSourceMLIJs = [];
		let tmpWarnings = [];

		this.log.info(`Sample Plan Comprehension (in-flight dest) Builder`);
		this.log.info(`  Source GUIDProject:    ${tmpSourceGUIDProject}`);
		this.log.info(`  Dest GUIDProject:      ${tmpDestGUIDProject}`);
		this.log.info(`  Dest LineItems given:  ${tmpDestLineItems.length}`);

		this.fable.Utility.waterfall(
			[
				// Stage 1: Look up source project by GUID. Missing source = silent skip.
				(fStageComplete) =>
				{
					this.lookupProjectByGUID(tmpSourceGUIDProject,
						(pError, pRecord) =>
						{
							if (pError)
							{
								this.log.error(`Error looking up source project by GUID`, pError);
								return fStageComplete(pError);
							}
							if (!pRecord)
							{
								this.log.info(`  Source project [${tmpSourceGUIDProject}] not found — skipping sample plan section.`);
								return fStageComplete('SKIP');
							}
							tmpSourceProject = pRecord;
							this.log.info(`  Source resolved:       [${tmpSourceProject.IDProject}] ${tmpSourceProject.Name || '(unnamed)'}`);
							return fStageComplete();
						});
				},

				// Stage 2: Count source line items (stats only).
				(fStageComplete) =>
				{
					this.restClient.getEntitySetCount('LineItem',
						`FBV~IDProject~EQ~${tmpSourceProject.IDProject}~FBV~Deleted~EQ~0`,
						(pError, pResult) =>
						{
							if (pError)
							{
								this.log.error(`Error counting source line items`, pError);
								return fStageComplete(pError);
							}
							tmpSourceLineItemCount = (pResult && pResult.Count) ? pResult.Count : 0;
							this.log.info(`  Source project has ${tmpSourceLineItemCount} total line items.`);
							return fStageComplete();
						});
				},

				// Stage 3: Fetch matching source line items by ItemCode (only those that match dest).
				(fStageComplete) =>
				{
					let tmpDestItemCodes = {};
					for (let i = 0; i < tmpDestLineItems.length; i++)
					{
						if (tmpDestLineItems[i].ItemCode)
						{
							tmpDestItemCodes[tmpDestLineItems[i].ItemCode] = true;
						}
					}
					let tmpUniqueItemCodes = Object.keys(tmpDestItemCodes);
					this.log.info(`  Destination has ${tmpUniqueItemCodes.length} unique ItemCodes.`);

					if (tmpUniqueItemCodes.length === 0)
					{
						this.log.warn(`  No ItemCodes found in destination — nothing to match.`);
						return fStageComplete();
					}

					this.getEntitySetBatched('LineItem', 'ItemCode', tmpUniqueItemCodes,
						`FBV~IDProject~EQ~${tmpSourceProject.IDProject}`,
						(pError, pRecords) =>
						{
							if (pError)
							{
								this.log.error(`Error fetching source line items`, pError);
							}
							else
							{
								tmpSourceLineItems = pRecords;
								this.log.info(`  Found ${tmpSourceLineItems.length} matching source line items.`);
							}
							return fStageComplete(pError);
						});
				},

				// Stage 4: Fetch source PLIMTs.
				(fStageComplete) =>
				{
					let tmpSourceLineItemIDs = tmpSourceLineItems.map((pLI) => pLI.IDLineItem);
					if (tmpSourceLineItemIDs.length === 0) return fStageComplete();

					this.getEntitySetBatched('ProjectLineItemMaterialTest', 'IDLineItem', tmpSourceLineItemIDs, null,
						(pError, pRecords) =>
						{
							if (pError)
							{
								this.log.error(`Error fetching source PLIMTs`, pError);
							}
							else
							{
								tmpSourcePLIMTs = pRecords;
								this.log.info(`  Found ${tmpSourcePLIMTs.length} source ProjectLineItemMaterialTests.`);
							}
							return fStageComplete(pError);
						});
				},

				// Stage 5: Fetch source MaterialLineItemJoins.
				(fStageComplete) =>
				{
					let tmpSourceLineItemIDs = tmpSourceLineItems.map((pLI) => pLI.IDLineItem);
					if (tmpSourceLineItemIDs.length === 0) return fStageComplete();

					this.getEntitySetBatched('MaterialLineItemJoin', 'IDLineItem', tmpSourceLineItemIDs, null,
						(pError, pRecords) =>
						{
							if (pError)
							{
								this.log.error(`Error fetching source MLIJs`, pError);
							}
							else
							{
								tmpSourceMLIJs = pRecords;
								this.log.info(`  Found ${tmpSourceMLIJs.length} source MaterialLineItemJoins.`);
							}
							return fStageComplete(pError);
						});
				},

				// Stage 6: Resolve the source's specification sets so the caller can check them against its contract.
				(fStageComplete) =>
				{
					this.getProjectContext(tmpSourceProject.IDProject,
						(pContextError, pContext) =>
						{
							if (pContextError)
							{
								this.log.warn(`Could not read the source project's contract; its specification sets are unknown.`, pContextError);
								return fStageComplete();
							}
							this.resolveProjectSpecificationSets(pContext, SPECIFICATION_SET_LINE_ITEM_SAMPLE,
								(pSetsError, pSets) =>
								{
									if (pSetsError)
									{
										this.log.warn(`Could not read the source project's line item pay items; its specification sets are unknown.`, pSetsError);
										return fStageComplete();
									}
									tmpSourceSets = pSets;
									return fStageComplete();
								});
						});
				},

				// Stage 7: Build the comprehension. Uses GUID refs (not IDs) for the dest FKs.
				(fStageComplete) =>
				{
					let tmpSourceIndex = this.indexLineItemsByItemCode(tmpSourceLineItems);
					let tmpSourceLineItemsByItemCode = tmpSourceIndex.ByItemCode;
					if (tmpSourceIndex.Warning)
					{
						this.log.warn(tmpSourceIndex.Warning);
						tmpWarnings.push(tmpSourceIndex.Warning);
					}

					let tmpSourcePLIMTsByIDLineItem = {};
					for (let i = 0; i < tmpSourcePLIMTs.length; i++)
					{
						let tmpPLIMT = tmpSourcePLIMTs[i];
						if (!tmpSourcePLIMTsByIDLineItem[tmpPLIMT.IDLineItem])
						{
							tmpSourcePLIMTsByIDLineItem[tmpPLIMT.IDLineItem] = [];
						}
						tmpSourcePLIMTsByIDLineItem[tmpPLIMT.IDLineItem].push(tmpPLIMT);
					}

					let tmpSourceMLIJsByIDLineItem = {};
					for (let i = 0; i < tmpSourceMLIJs.length; i++)
					{
						let tmpMLIJ = tmpSourceMLIJs[i];
						if (!tmpSourceMLIJsByIDLineItem[tmpMLIJ.IDLineItem])
						{
							tmpSourceMLIJsByIDLineItem[tmpMLIJ.IDLineItem] = [];
						}
						tmpSourceMLIJsByIDLineItem[tmpMLIJ.IDLineItem].push(tmpMLIJ);
					}

					let tmpComprehension = {
						ProjectLineItemMaterialTest: {},
						MaterialLineItemJoin: {}
					};

					let tmpMatchedCount = 0;
					let tmpUnmatchedCount = 0;
					let tmpPLIMTCount = 0;
					let tmpMLIJCount = 0;
					let tmpSeenMLIJKeys = {};

					for (let i = 0; i < tmpDestLineItems.length; i++)
					{
						let tmpDestLI = tmpDestLineItems[i];
						let tmpSourceLI = tmpSourceLineItemsByItemCode[tmpDestLI.ItemCode];

						if (!tmpSourceLI)
						{
							tmpUnmatchedCount++;
							continue;
						}

						tmpMatchedCount++;
						let tmpSourcePLIMTsForLI = tmpSourcePLIMTsByIDLineItem[tmpSourceLI.IDLineItem] || [];
						let tmpSourceMLIJsForLI = tmpSourceMLIJsByIDLineItem[tmpSourceLI.IDLineItem] || [];

						for (let j = 0; j < tmpSourcePLIMTsForLI.length; j++)
						{
							let tmpSrc = tmpSourcePLIMTsForLI[j];
							let tmpCompositeKey = `${tmpDestGUIDProject}|${tmpDestLI.GUIDLineItem}|${tmpSrc.IDMaterial}|${tmpSrc.IDTest}|${tmpSrc.SampleType}`;
							let tmpGUID = this.generateGUID(tmpGUIDPrefixPLIMT, tmpCompositeKey);

							tmpComprehension.ProjectLineItemMaterialTest[tmpGUID] = {
								GUIDProjectLineItemMaterialTest: tmpGUID,
								GUIDProject: tmpDestGUIDProject,
								GUIDLineItem: tmpDestLI.GUIDLineItem,
								IDMaterial: tmpSrc.IDMaterial,
								IDTest: tmpSrc.IDTest,
								IDLab: tmpSrc.IDLab || 0,
								Intent: tmpSrc.Intent || '',
								SampleType: tmpSrc.SampleType || '',
								SampleSize: tmpSrc.SampleSize || 0,
								SampleUnits: tmpSrc.SampleUnits || '',
								Rate: tmpSrc.Rate || 0,
								Frequency: tmpSrc.Frequency || 0,
								Units: tmpSrc.Units || '',
								TestDescription: tmpSrc.TestDescription || '',
								SampleDescription: tmpSrc.SampleDescription || '',
								SamplingInstructions: tmpSrc.SamplingInstructions || '',
								SamplingRequirements: tmpSrc.SamplingRequirements || '',
								SamplingPeriod: tmpSrc.SamplingPeriod || '',
								Remarks: tmpSrc.Remarks || '',
								ConversionFactor: tmpSrc.ConversionFactor || 1,
								CertificateRequired: tmpSrc.CertificateRequired || 0,
								TestsNotRequired: tmpSrc.TestsNotRequired || 0,
								ErrorOmission: tmpSrc.ErrorOmission || 0,
								ErrorOmissionDetail: tmpSrc.ErrorOmissionDetail || '',
								ManualPlannedSampleCount: -1,
								ManualRequiredSampleCount: -1,
								ComputedPlannedSampleCount: 0,
								ComputedRequiredSampleCount: 0,
								CreatedSampleCount: 0,
								PassingSampleCount: 0,
								FailingSampleCount: 0,
								Deleted: 0
							};
							tmpPLIMTCount++;
						}

						for (let j = 0; j < tmpSourceMLIJsForLI.length; j++)
						{
							let tmpSrc = tmpSourceMLIJsForLI[j];
							let tmpMLIJKey = `${tmpDestLI.GUIDLineItem}|${tmpSrc.IDMaterial}`;

							if (tmpSeenMLIJKeys[tmpMLIJKey]) continue;
							tmpSeenMLIJKeys[tmpMLIJKey] = true;

							let tmpGUID = this.generateGUID(tmpGUIDPrefixMLJ, tmpMLIJKey);

							tmpComprehension.MaterialLineItemJoin[tmpGUID] = {
								GUIDMaterialLineItemJoin: tmpGUID,
								GUIDLineItem: tmpDestLI.GUIDLineItem,
								IDMaterial: tmpSrc.IDMaterial,
								ConversionFactor: tmpSrc.ConversionFactor || 1,
								ApprovedSourceRequired: tmpSrc.ApprovedSourceRequired || 0,
								SpecialInstructions: tmpSrc.SpecialInstructions || '',
								Units: tmpSrc.Units || '',
								SampleRate: tmpSrc.SampleRate || 0,
								SampleFrequency: tmpSrc.SampleFrequency || 0,
								RequiredSampleCount: tmpSrc.RequiredSampleCount || 0,
								Quantity: tmpSrc.Quantity || 0,
								Deleted: 0
							};
							tmpMLIJCount++;
						}
					}

					this.log.info(`=== Comprehension (in-flight dest) Built ===`);
					this.log.info(`  Matched / Unmatched dest LineItems: ${tmpMatchedCount} / ${tmpUnmatchedCount}`);
					this.log.info(`  PLIMT: ${tmpPLIMTCount}, MLJ: ${tmpMLIJCount}`);

					if (tmpUnmatchedCount > 0)
					{
						tmpWarnings.push(`${tmpUnmatchedCount} destination line items have no line item with the same item code in source [${tmpSourceGUIDProject}].`);
					}

					let tmpReportData = {
						sourceProject: tmpSourceProject,
						destGUIDProject: tmpDestGUIDProject,
						specificationSets: { Source: tmpSourceSets },
						stats: {
							sourceLineItemCount: tmpSourceLineItemCount,
							destLineItemCount: tmpDestLineItems.length,
							matched: tmpMatchedCount,
							unmatched: tmpUnmatchedCount,
							plimtCount: tmpPLIMTCount,
							mljCount: tmpMLIJCount
						}
					};

					return fStageComplete(null, { comprehension: tmpComprehension, reportData: tmpReportData, warnings: tmpWarnings });
				}
			],
			(pError, pResult) =>
			{
				// Sentinel "SKIP" means the source project didn't exist — surface null result, not an error.
				if (pError === 'SKIP')
				{
					return fCallback(null, null);
				}
				if (pError)
				{
					this.log.error(`Sample plan (in-flight dest) comprehension build failed.`, pError);
				}
				return fCallback(pError, pResult);
			});
	}

	/**
	 * @typedef {object} SamplePlanEntityLoadResult
	 * @property {string} Entity - The Meadow entity.
	 * @property {number} Sent - Records in the comprehension.
	 * @property {number} Succeeded - Records the server accepted.
	 * @property {number} Failed - Records that failed to marshal or were refused by the server.
	 * @property {string} Status - 'Loaded', 'Errors', or 'Not attempted' when an earlier entity failed.
	 */

	/**
	 * Load a sample plan comprehension into the destination project via the Headlight Integration
	 * Adapter (batch upserts with retry). Entities load in order -- Contract, LineItem,
	 * ProjectLineItemMaterialTest, MaterialLineItemJoin -- and a failed entity stops the ones after it.
	 *
	 * @param {Record<string, Record<string, any>>} pComprehension - Output of `buildComprehension` or `buildComprehensionFromLineItems`.
	 * @param {(pError?: any, pResult?: {entities: Array<SamplePlanEntityLoadResult>}) => void} fCallback - Callback.
	 */
	loadComprehension(pComprehension, fCallback)
	{
		let tmpEntityPlan = [
			{ Entity: 'Contract' },
			{ Entity: 'LineItem' },
			{ Entity: 'ProjectLineItemMaterialTest', EntityGUIDMarshalPrefix: 'MSP-PLIMT' },
			{ Entity: 'MaterialLineItemJoin', EntityGUIDMarshalPrefix: 'MSP-MLJ' }
		];

		// Register the IntegrationAdapter service type if not already registered
		if (!this.fable.servicesMap.IntegrationAdapter)
		{
			this.fable.addServiceType('IntegrationAdapter', libIntegrationAdapter);
		}

		let tmpAdapterOptions = {
			ForceMarshal: true,
			PerformDeletes: false,
			AllowGUIDTruncation: true,
		};

		let tmpResults = [];
		let tmpStages = [];
		for (let i = 0; i < tmpEntityPlan.length; i++)
		{
			let tmpPlan = tmpEntityPlan[i];
			let tmpRecords = pComprehension[tmpPlan.Entity] || {};
			let tmpKeys = Object.keys(tmpRecords);
			if (tmpKeys.length === 0)
			{
				continue;
			}

			let tmpResult = { Entity: tmpPlan.Entity, Sent: tmpKeys.length, Succeeded: 0, Failed: 0, Status: 'Not attempted' };
			tmpResults.push(tmpResult);
			tmpStages.push(
				(fStageComplete) =>
				{
					// Create fresh adapter instances each load (not cached via getAdapter)
					// so that source/marshaled record buffers are clean between runs.
					let tmpAdapter = this.fable.instantiateServiceProviderWithoutRegistration('IntegrationAdapter', Object.assign({}, tmpAdapterOptions, tmpPlan));
					for (let j = 0; j < tmpKeys.length; j++)
					{
						tmpAdapter.addSourceRecord(tmpRecords[tmpKeys[j]]);
					}

					this.log.info(`Integrating ${tmpKeys.length} ${tmpPlan.Entity} records...`);
					tmpAdapter.integrateRecords(
						(pError) =>
						{
							let tmpLanded = Object.keys(tmpAdapter._MarshaledRecords).length - tmpAdapter.RecordPushFailures.length;
							tmpResult.Succeeded = Math.max(0, tmpLanded);
							tmpResult.Failed = tmpResult.Sent - tmpResult.Succeeded;
							tmpResult.Status = (pError || (tmpResult.Failed > 0)) ? 'Errors' : 'Loaded';
							if (pError)
							{
								this.log.error(`Error integrating ${tmpPlan.Entity} records`, pError);
							}
							return fStageComplete(pError);
						});
				});
		}

		this.log.info(`Loading comprehension via Integration Adapter: ${tmpResults.map((pResult) => `${pResult.Sent} ${pResult.Entity}`).join(', ')}`);
		if (tmpStages.length === 0)
		{
			return fCallback(null, { entities: tmpResults });
		}

		this.fable.Utility.waterfall(tmpStages,
			(pError) =>
			{
				this.log.info(`=== Comprehension Load Complete ===`);
				for (let i = 0; i < tmpResults.length; i++)
				{
					this.log.info(`  ${tmpResults[i].Entity}: ${tmpResults[i].Succeeded} of ${tmpResults[i].Sent} succeeded, ${tmpResults[i].Failed} failed (${tmpResults[i].Status})`);
				}
				return fCallback(pError, { entities: tmpResults });
			});
	}
}

module.exports = SamplePlanService;

module.exports.default_configuration = {
	Name: 'SamplePlanService'
};

module.exports.INN_VALUE_CHARACTER_BUDGET = INN_VALUE_CHARACTER_BUDGET;
